import translations from "../components/common/Translations";

// Shared limits for every multi-file upload (Interactive/Special RC, bulk RC,
// instalment plan create/edit). The backend re-checks both and answers 601
// with a readable message, so these only spare the user a round trip.
// The per-file size is ops-configurable (10 MB by default, 5 MB on some
// environments) through REACT_APP_MAX_ATTACHMENT_SIZE_MB.
export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_SIZE_MB =
  Number(process.env.REACT_APP_MAX_ATTACHMENT_SIZE_MB) || 10;
const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

export const ALLOWED_ATTACHMENT_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
];

const t = (langMode) => translations[langMode] || translations.en;

export const maxAttachmentsMessage = (langMode) =>
  (t(langMode).maxAttachmentsExceeded || "You can upload a maximum of {count} attachments")
    .replace("{count}", MAX_ATTACHMENTS);

export const attachmentTooLargeMessage = (langMode) =>
  (t(langMode).attachmentTooLarge || "File exceeds the maximum allowed size of {size} MB")
    .replace("{size}", MAX_ATTACHMENT_SIZE_MB);

/**
 * Merges newly picked files into the current selection. Returns the files to
 * keep plus the first error to show; a rejected pick leaves `current` as-is
 * so one bad file never wipes what was already chosen.
 */
export const mergeAttachments = (current, incoming, langMode) => {
  const picked = Array.from(incoming || []);
  if (picked.length === 0) return { files: current, error: "" };

  if (picked.some((f) => !ALLOWED_ATTACHMENT_TYPES.includes(f.type))) {
    return { files: current, error: t(langMode).onlypdfimgallowed };
  }
  if (picked.some((f) => f.size > MAX_ATTACHMENT_SIZE_BYTES)) {
    return { files: current, error: attachmentTooLargeMessage(langMode) };
  }
  if (current.length + picked.length > MAX_ATTACHMENTS) {
    return { files: current, error: maxAttachmentsMessage(langMode) };
  }
  return { files: [...current, ...picked], error: "" };
};

// Last-line check before submitting, in case files reached state some other way.
export const validateAttachments = (files, langMode) => {
  if (files.length > MAX_ATTACHMENTS) return maxAttachmentsMessage(langMode);
  if (files.some((f) => f.size > MAX_ATTACHMENT_SIZE_BYTES)) {
    return attachmentTooLargeMessage(langMode);
  }
  return "";
};

// /instalment-plan/get returns every URL in `attachments`. The legacy single
// `attachment` field is being dropped by the backend, so it is not read here.
export const getPlanAttachmentUrls = (plan) =>
  Array.isArray(plan?.attachments) ? plan.attachments.filter(Boolean) : [];

// Identity of a history attachment across refetches -- downloadUrl is a
// presigned link that changes every call, so it can't be part of the key.
export const attachmentKey = (a) =>
  [a?.source, a?.taskId, a?.instalmentPlanId, a?.fileName, a?.uploadedAt].join("|");

export const findAttachment = (list, target) => {
  const key = attachmentKey(target);
  return (list || []).find((a) => attachmentKey(a) === key) || null;
};

// Extension of a file name or URL, ignoring any query/hash suffix.
const extensionOf = (value) => {
  if (typeof value !== "string") return "";
  const clean = value.split("?")[0].split("#")[0];
  const dot = clean.lastIndexOf(".");
  return dot === -1 ? "" : clean.slice(dot + 1).toLowerCase();
};

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic", "heif"];

// "image" | "pdf" | "file", judged from the file name first, then the URL.
export const attachmentKind = (fileName, url) => {
  const ext = extensionOf(fileName) || extensionOf(url);
  if (IMAGE_EXTENSIONS.includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  return "file";
};

// Readable file name from a URL's last path segment, or `fallback`.
export const fileNameFromUrl = (url, fallback) => {
  if (typeof url !== "string") return fallback;
  try {
    const clean = url.split("?")[0].split("#")[0];
    return decodeURIComponent(clean.substring(clean.lastIndexOf("/") + 1)) || fallback;
  } catch (_) {
    return fallback;
  }
};

const clickLink = (href, fileName) => {
  const link = document.createElement("a");
  link.href = href;
  if (fileName) link.download = fileName;
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

// ODP object storage (oss.odp.om) sends no CORS headers, so fetch() can't
// read its files, and the backend sends no downloadUrl for them. Unlike AWS,
// it honours response-content-disposition on an unsigned public URL, which
// turns the plain view link into one the browser saves in place.
const ODP_HOST = "oss.odp.om";

const odpDownloadUrl = (url, fileName) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== ODP_HOST) return null;
    const name = (fileName || fileNameFromUrl(url, "attachment")).replace(/"/g, "");
    parsed.searchParams.set("response-content-disposition", `attachment; filename="${name}"`);
    return parsed.toString();
  } catch (_) {
    return null;
  }
};

/**
 * Saves `url` in the current tab, without opening a new one. The file is
 * fetched as a blob so the `download` name applies even cross-origin. If the
 * bucket refuses CORS, a link served with Content-Disposition: attachment
 * still downloads in place when followed: the backend's presigned
 * downloadUrl (`isAttachmentUrl`), or an ODP view link with that header
 * requested. Any other plain view link would replace the app, so only that
 * case falls back to a new tab.
 */
export const downloadFile = async (url, fileName, { isAttachmentUrl = false } = {}) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const objectUrl = URL.createObjectURL(await response.blob());
    clickLink(objectUrl, fileName);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch (error) {
    console.error("Blob download failed, falling back to the link:", error);
    const attachmentUrl = isAttachmentUrl ? url : odpDownloadUrl(url, fileName);
    if (attachmentUrl) {
      clickLink(attachmentUrl, fileName);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }
};
